import EditRounded from "@mui/icons-material/EditRounded";
import { Button, Grid, LinearProgress, Typography } from "@mui/material";
import {
  BulkActionType,
  getDefaultPlannedCreditMap,
  IBulkEventPayload,
  ICreditClientSettingSetValue,
  IDisplayPairing,
  IPairingBuilderDefinition,
  IPairingBuilderDefinitionPositions,
  IPairingLogicModifier,
  IPlannedCreditMap,
  setPlannedCreditInMap,
} from "@vmosolutions/oc-interfaces";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { IconButton } from "src/components/IconButton/IconButton";
import { updateWaterfallPairings } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { openWindow } from "src/redux/actions/Mosaic.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { v1 } from "uuid";

// Definition Controls
import { cloneDeep, debounce, isEqual } from "lodash";
import { DefinitionBiddable } from "./Controls/DefinitionBiddable";
import { DefinitionEnd } from "./Controls/DefinitionEnd";
import { DefinitionIncludeSplits } from "./Controls/DefinitionIncludeSplits";
import { DefinitionName } from "./Controls/DefinitionName";
import { DefinitionPlannedCredit } from "./Controls/DefinitionPlannedCredit";
import { DefinitionPositions } from "./Controls/DefinitionPositions";
import { DefinitionRaw } from "./Controls/DefinitionRaw";
import { DefinitionStart } from "./Controls/DefinitionStart";
import { DefinitionWeekdays } from "./Controls/DefinitionWeekdays";
import { TextField } from "src/components/FormikComponents/TextField";
// Add/Remove Icons
import AddBox from "@mui/icons-material/AddBox";
import RemoveBox from "@mui/icons-material/IndeterminateCheckBox";
import CircularProgress from "@mui/material/CircularProgress";
import { DefinitionCheckInOffset } from "./Controls/DefinitionCheckInOffset";
import { DefinitionLMSelector } from "./Controls/DefinitionLogicModifiers";

interface IPairingDefinitionBuilderProps {
  definition: IPairingBuilderDefinition;
  updateDefinition: (newPattern: IPairingBuilderDefinition, index: number) => IPairingBuilderDefinition;
  index: number;
  setActiveDefinition: (index: number) => void;
  active: boolean; // Definition displays controls only when focused (active).
  start: number;
  end: number;
  includeDuplicates?: boolean;
  includeUnassignedFlights?: boolean;
  AddNewDefinition: (index: number) => void;
  removeDefinition: (index: number, name: string) => void;
  creditSettingValue?: ICreditClientSettingSetValue;
}

export const PairingDefinitionBuilder = (props: IPairingDefinitionBuilderProps): JSX.Element => {
  const {
    updateDefinition,
    index,
    setActiveDefinition,
    active,
    start,
    end,
    includeDuplicates,
    includeUnassignedFlights,
    AddNewDefinition,
    removeDefinition,
    creditSettingValue,
  } = props;

  const [definition, setDefinition] = useState<IPairingBuilderDefinition>(props.definition);

  useEffect(() => {
    setDefinition(props.definition);
  }, [props.definition]);

  const [watchingBulkId, setWatchingBulkId] = useState<string | null>(null);

  const bulkOps = useSelector((state: IRootState) => state.bulkBatch);
  const dispatch = useDispatch();
  const pairingDefBulkEvent = useMemo(() => {
    return bulkOps[watchingBulkId];
  }, [bulkOps, watchingBulkId]);

  const bulkProgress: number = useMemo(() => {
    if (!pairingDefBulkEvent) {
      return 0;
    }
    const totalCount = pairingDefBulkEvent.records;
    const completed = pairingDefBulkEvent?.successfulEventIds?.length ?? 0;
    const failed = pairingDefBulkEvent?.failedEvents?.length ?? 0;
    const percentageComplete = totalCount === 0 ? 0 : ((completed + failed) * 100) / totalCount;
    return percentageComplete;
  }, [pairingDefBulkEvent]);

  const [statusMessage, setStatusMessage] = useState<string>("");
  useEffect(() => {
    if (pairingDefBulkEvent) {
      if (!pairingDefBulkEvent) {
        setStatusMessage("");
      }
      const { records, successfulEventIds, failedEvents, actionType } = pairingDefBulkEvent;
      const failedUpdates = failedEvents?.length > 0 ? `, ${failedEvents?.length} failed` : "";
      const message = `${successfulEventIds?.length} of ${records} pairings successfully ${
        actionType === BulkActionType.CREATE_PAIRING ? "built" : "deleted"
      }${failedUpdates}`;
      // Build completed
      if (successfulEventIds?.length + failedEvents?.length === records) {
        setStatusMessage(message ?? "");
      }
    }
  }, [pairingDefBulkEvent]);
  /**
   * Retrieve pairings this definition would create
   * Pairings will be returned as temporary or real based on the "build" param passed in.
   */
  const [loadingTempPairings, setLoadingTempPairings] = useState<boolean>(false);
  const [buildingPairings, setBuildingPairings] = useState<boolean>(false);
  const [noMatchesFound, setNoMatchesFound] = useState<boolean>(false);
  const [realPairingsBuilt, setRealPairingsBuilt] = useState<boolean>(false);
  const getTempPairings = useCallback(
    async (defToUse: IPairingBuilderDefinition, build = false) => {
      if (!build) {
        setLoadingTempPairings(true);
      } else {
        setBuildingPairings(true);
      }
      setRealPairingsBuilt(false);
      setNoMatchesFound(false);
      const bulkId = v1();
      setWatchingBulkId(bulkId);
      const payload = { start, end, definition: defToUse, bulkId, includeDuplicates, includeUnassignedFlights };
      const results: IDisplayPairing[] = await AmplifyAPIWrapper.Post<any>(
        `/pairingbuilder/definition?build=${build}`,
        payload,
        {}
      );
      if (Array.isArray(results) && results?.length) {
        dispatch(
          updateWaterfallPairings(
            results.sort((a, b) => a.start?.valueOf() - b.start?.valueOf() || a.end?.valueOf() - b.end?.valueOf())
          )
        );
        dispatch(openWindow("Pairing Waterfall", "PairingWaterfallWindow", [], {}, "context", true));
        if (build) setRealPairingsBuilt(true);
      } else {
        setNoMatchesFound(true);
      }
      setLoadingTempPairings(false);
      setBuildingPairings(false);
    },
    [start, end, includeDuplicates, includeUnassignedFlights, dispatch]
  );

  /**
   * Allow users to revert (delete) any pairings they have built in case it was accidental.
   */
  const [revertBuilding, setRevertBuilding] = useState<boolean>(false);
  const revertPairings = useCallback(async () => {
    setRevertBuilding(true);
    const bulkId = v1();
    const bulkPayload: IBulkEventPayload = {
      actionType: BulkActionType.DELETE_PAIRING,
      batchIds: pairingDefBulkEvent?.successfulEventIds.map((e) => e.objectId),
      payload: null, // what is to be updated in the DB - null for delete
      bulkId: bulkId,
    };
    setWatchingBulkId(bulkId);
    await AmplifyAPIWrapper.Post<any>("/bulk", bulkPayload, {});
    setRevertBuilding(false);
    // Unlock the form so the user can keep iterating on this definition.
    setRealPairingsBuilt(false);
  }, [pairingDefBulkEvent?.successfulEventIds]);

  const nameInitialValue: string = definition?.name ?? `Build Definition ${index}`;
  const rawInitialValue: string = definition?.raw;
  const positionsInitialValue: IPairingBuilderDefinitionPositions[] = useMemo(
    () => definition?.positions ?? [],
    [definition?.positions]
  );
  const weekdaysInitialValue: number[] = useMemo(
    () => definition?.recurrenceSimple?.daysOfWeek ?? [],
    [definition?.recurrenceSimple?.daysOfWeek]
  );
  const checkInOffsetInitialValue: number | undefined = useMemo(
    () => definition?.checkInOffset ?? undefined,
    [definition?.checkInOffset]
  );
  const plannedCreditInitialValue: number = definition?.plannedCredit;
  const plannedCreditMapInitialValue: IPlannedCreditMap = useMemo(
    //because it is a map - I need to clone deep
    () => cloneDeep(props.definition.plannedCreditMap ?? {}),
    [props.definition.plannedCreditMap]
  );
  const includeSplitsInitialValue: boolean = definition?.includeSplits;
  const isBiddableInitialValue: boolean = definition?.isBiddable;
  const startInitialValue = useMemo(
    //@ts-ignore - this is coming in as a string when it should be a number
    () => (definition?.absStart ? parseInt(definition?.absStart) : null),
    [definition?.absStart]
  );
  const endInitialValue = useMemo(
    //@ts-ignore - this is coming in as a string when it should be a number
    () => (definition?.absEnd ? parseInt(definition?.absEnd) : null),
    [definition?.absEnd]
  );
  const logicModifiersInitialValue: IPairingLogicModifier[] = useMemo(
    () => definition?.logicModifiers ?? [],
    [definition?.logicModifiers]
  );
  // Control States (Track state of values)
  const [newName, setNewName] = useState<string>(nameInitialValue);
  const [newRaw, setNewRaw] = useState<string>(rawInitialValue);
  const [newPositions, setNewPositions] = useState<IPairingBuilderDefinitionPositions[]>(positionsInitialValue);
  const [newWeekdays, setNewWeekdays] = useState<number[]>(weekdaysInitialValue);
  const [newCheckInOffset, setCheckInOffset] = useState<number | undefined>(checkInOffsetInitialValue);
  const [newPlannedCredit, setNewPlannedCredit] = useState<number>(plannedCreditInitialValue);
  const [newIncludeSplits, setNewIncludeSplits] = useState<boolean>(includeSplitsInitialValue);
  const [newIsBiddable, setNewIsBiddable] = useState<boolean>(isBiddableInitialValue);
  const [newStart, setNewStart] = useState<number | null>(startInitialValue);
  const [newEnd, setNewEnd] = useState<number | null>(endInitialValue);
  const [newLogicModifiers, setNewLogicModifiers] = useState<IPairingLogicModifier[]>(logicModifiersInitialValue);
  const [plannedCreditMap, setPlannedCreditMap] = useState<IPlannedCreditMap>(plannedCreditMapInitialValue);

  // Sync form fields to the decoded definition whenever it arrives from the parent.
  // decodePairingDefinition (in the parent) can transform values on save, so the form
  // shows the actual saved state rather than the user's raw input after View/Build.
  useEffect(() => {
    const def = props.definition;
    setNewName(def?.name ?? `Build Definition ${index}`);
    setNewRaw(def?.raw);
    setNewPositions(def?.positions ?? []);
    setNewWeekdays(def?.recurrenceSimple?.daysOfWeek ?? []);
    setCheckInOffset(def?.checkInOffset ?? undefined);
    setNewPlannedCredit(def?.plannedCredit);
    setPlannedCreditMap(cloneDeep(def?.plannedCreditMap ?? {}));
    setNewIncludeSplits(def?.includeSplits);
    setNewIsBiddable(def?.isBiddable);
    //@ts-ignore - absStart comes in as a string when it should be a number
    setNewStart(def?.absStart ? parseInt(def?.absStart) : null);
    //@ts-ignore - absEnd comes in as a string when it should be a number
    setNewEnd(def?.absEnd ? parseInt(def?.absEnd) : null);
    setNewLogicModifiers(def?.logicModifiers ?? []);
  }, [props.definition, index]);

  /**
   * Push current form values up to the parent (where they're decoded) and return the
   * decoded definition so callers can hand it straight to the backend without waiting
   * on a props.definition round-trip.
   */
  const saveDefinition = useCallback(
    (index: number): IPairingBuilderDefinition => {
      const updatedValue: IPairingBuilderDefinition = {
        ...definition,
        name: newName,
        raw: newRaw,
        positions: newPositions,
        recurrenceSimple: { ...definition.recurrenceSimple, daysOfWeek: newWeekdays },
        plannedCredit: newPlannedCredit,
        plannedCreditMap: plannedCreditMap,
        isBiddable: newIsBiddable,
        includeSplits: newIncludeSplits,
        checkInOffset: newCheckInOffset,
        absStart: newStart,
        absEnd: newEnd,
        logicModifiers: newLogicModifiers,
      };
      return updateDefinition(updatedValue, index);
    },
    [
      definition,
      newName,
      newRaw,
      newPositions,
      newWeekdays,
      newPlannedCredit,
      plannedCreditMap,
      newIsBiddable,
      newIncludeSplits,
      newCheckInOffset,
      newStart,
      newEnd,
      newLogicModifiers,
      updateDefinition,
    ]
  );

  // Combined save + fetch. View and Build both go through this so the user only
  // has to click once: the form persists to the parent and the backend fetch
  // fires with the freshly-decoded definition.
  const handleView = useCallback(
    async (build = false) => {
      const decodedDef = saveDefinition(index);
      await getTempPairings(decodedDef, build);
    },
    [saveDefinition, index, getTempPairings]
  );

  // Track dirty states
  const nameIsDirty = nameInitialValue !== newName;
  const rawIsDirty = rawInitialValue !== newRaw;
  const positionsIsDirty = !isEqual(positionsInitialValue, newPositions);
  const weekdaysIsDirty = !isEqual(weekdaysInitialValue, newWeekdays);
  const checkInOffsetIsDirty = !isEqual(checkInOffsetInitialValue, newCheckInOffset);
  const plannedCreditIsDirty = plannedCreditInitialValue !== newPlannedCredit;
  const plannedCreditMapIsDirty = !isEqual(plannedCreditMapInitialValue, plannedCreditMap);
  const isBiddableIsDirty = isBiddableInitialValue !== newIsBiddable;
  const includeSplitsIsDirty = includeSplitsInitialValue !== newIncludeSplits;
  const startIsDirty = startInitialValue !== newStart;
  const endIsDirty = endInitialValue !== newEnd;
  const logicModifiersIsDirty = !isEqual(logicModifiersInitialValue, newLogicModifiers);
  const definitionIsDirty =
    nameIsDirty ||
    rawIsDirty ||
    positionsIsDirty ||
    weekdaysIsDirty ||
    plannedCreditIsDirty ||
    plannedCreditMapIsDirty ||
    isBiddableIsDirty ||
    includeSplitsIsDirty ||
    startIsDirty ||
    endIsDirty ||
    checkInOffsetIsDirty ||
    logicModifiersIsDirty;
  /**
   * Field Validation by diabling buttons
   */
  const isRequiredFieldsFilled = useMemo(() => {
    // Positions Field Checking
    const positionSum = newPositions.reduce((sum, pos) => (sum += isNaN(pos.count) ? 0 : pos.count), 0);
    const positionsFilled = positionSum > 0;

    // Definition Field Checking
    const definitionNotEmpty = newRaw.length > 0;

    // Weekday Checking
    const weekdaysFilled = newWeekdays.length > 0;

    return positionsFilled && definitionNotEmpty && weekdaysFilled;
  }, [newPositions, newRaw, newWeekdays]);

  // Controls view and build button functionality. View/Build no longer gate on dirty
  // state because they save the form as part of their click; once a build succeeds
  // the form locks via realPairingsBuilt until the user reverts.
  const disableButtons = useMemo(() => {
    return !definition || !active || !start || !end || !isRequiredFieldsFilled || realPairingsBuilt;
  }, [active, definition, end, start, isRequiredFieldsFilled, realPairingsBuilt]);

  return (
    <Grid item className={`pairing_definition ${active ? "active" : ""} ${definitionIsDirty ? "dirty" : ""}`}>
      <Grid container direction="column">
        <Grid container className="status" wrap="nowrap" alignItems="center">
          <Grid container className="definition_details" alignItems="center" wrap="nowrap" style={{ width: 200 }}>
            <IconButton
              id="rrule_edit_mode_btn"
              className="edit_mode_button"
              onClick={() => setActiveDefinition(index)}
              disabled={active}
              size="large"
            >
              <EditRounded />
            </IconButton>
            <Typography variant="caption" className="definition_name" style={{ width: "unset" }}>
              {`${index + 1})`}
            </Typography>
            <DefinitionName
              initialValue={nameInitialValue}
              value={newName}
              onChange={setNewName}
              disabled={realPairingsBuilt}
            />
          </Grid>

          {pairingDefBulkEvent && bulkProgress > 0 && (
            <Grid container className="bulk_summary" alignItems="center" style={{ width: "calc(100% - 200px)" }}>
              {pairingDefBulkEvent && bulkProgress === 100 ? ( // If complete show status.
                <Typography variant="body2">{statusMessage}</Typography>
              ) : (
                // Else show progress indicator.
                <div style={{ width: "100%" }}>
                  <LinearProgress className="loading_bar" variant="determinate" value={50} />
                </div>
              )}
            </Grid>
          )}
        </Grid>

        <Grid container className={`controls ${active ? "show" : ""}`}>
          <DefinitionRaw
            initialValue={rawInitialValue}
            value={newRaw}
            onChange={setNewRaw}
            disabled={realPairingsBuilt}
          />
          <DefinitionPositions
            initialValue={positionsInitialValue}
            value={newPositions}
            onChange={setNewPositions}
            disabled={realPairingsBuilt}
          />
          <DefinitionWeekdays
            initialValue={weekdaysInitialValue}
            value={newWeekdays}
            onChange={setNewWeekdays}
            disabled={realPairingsBuilt}
          />
          <DefinitionLMSelector
            initialValue={logicModifiersInitialValue}
            value={newLogicModifiers}
            onChange={setNewLogicModifiers}
            disabled={realPairingsBuilt}
          />
          <DefinitionCheckInOffset
            initialValue={checkInOffsetInitialValue}
            value={newCheckInOffset}
            onChange={setCheckInOffset}
            disabled={realPairingsBuilt}
          />
          {!creditSettingValue?.hidePlannedCreditField && (
            <DefinitionPlannedCredit
              initialValue={plannedCreditInitialValue}
              value={newPlannedCredit}
              onChange={setNewPlannedCredit}
              disabled={realPairingsBuilt}
            />
          )}
          {!!plannedCreditMapInitialValue &&
            Object.keys(plannedCreditMapInitialValue).map((key) => {
              const initialCredit = plannedCreditMapInitialValue?.[key]?.credit ?? 0;
              const currentCredit = plannedCreditMap?.[key]?.credit ?? 0;
              const displayName =
                plannedCreditMap?.[key]?.displayName ?? plannedCreditMapInitialValue?.[key]?.displayName ?? key;
              return (
                <Grid item key={key}>
                  <TextField
                    className={`plannedcredit_control ${currentCredit !== initialCredit ? "dirty" : ""}`}
                    label={displayName}
                    name={key}
                    type="number"
                    value={plannedCreditMap?.[key]?.credit}
                    onChange={(event) => {
                      const newCredit = event?.target?.value ? parseFloat(event?.target?.value) : 0;
                      const newCreditMap = setPlannedCreditInMap(plannedCreditMap, key, newCredit);
                      setPlannedCreditMap(newCreditMap);
                    }}
                    fullWidth={false}
                    meta={{
                      value: currentCredit,
                      touched: currentCredit !== initialCredit,
                      initialValue: initialCredit,
                    }}
                    disabled={realPairingsBuilt}
                  />
                </Grid>
              );
            })}
          <DefinitionIncludeSplits
            initialValue={includeSplitsInitialValue}
            value={newIncludeSplits}
            onChange={setNewIncludeSplits}
            disabled={realPairingsBuilt}
          />
          <DefinitionBiddable
            initialValue={isBiddableInitialValue}
            value={newIsBiddable}
            onChange={setNewIsBiddable}
            disabled={realPairingsBuilt}
          />
          <DefinitionStart
            initialValue={startInitialValue}
            value={newStart}
            onChange={setNewStart}
            disabled={realPairingsBuilt}
          />
          <DefinitionEnd
            initialValue={endInitialValue}
            value={newEnd}
            onChange={setNewEnd}
            disabled={realPairingsBuilt}
          />
        </Grid>

        {active && (
          <Grid container direction="column">
            <Grid container wrap="nowrap" className="actions">
              <Button
                variant="outlined"
                onClick={debounce(() => handleView(false), 1000)}
                disabled={disableButtons || loadingTempPairings || buildingPairings}
              >
                View Matching Pairings
                {loadingTempPairings && (
                  <CircularProgress
                    color="inherit"
                    style={{ marginLeft: 10, marginRight: 0, width: 14, height: 14 }}
                  />
                )}
              </Button>

              <Button
                variant="contained"
                color="primary"
                onClick={debounce(() => handleView(true), 1000)}
                disabled={disableButtons || loadingTempPairings || buildingPairings}
              >
                Build Matching Pairings
                {buildingPairings && (
                  <CircularProgress color="inherit" style={{ marginLeft: 10, marginRight: 0, width: 14, height: 14 }} />
                )}
              </Button>

              <Button
                variant="contained"
                color="secondary"
                onClick={debounce(() => {
                  setStatusMessage("");
                  revertPairings();
                }, 1000)}
                disabled={
                  !realPairingsBuilt ||
                  bulkProgress < 100 ||
                  pairingDefBulkEvent?.actionType === BulkActionType.DELETE_PAIRING
                }
              >
                Revert Build
                {revertBuilding && (
                  <CircularProgress color="inherit" style={{ marginLeft: 10, marginRight: 0, width: 14, height: 14 }} />
                )}
              </Button>

              <Grid container className="add_remove_buttons" wrap="nowrap">
                <IconButton
                  tooltipProps={{ title: "Insert new definition after this one." }}
                  className="add_definition_btn"
                  onClick={() => AddNewDefinition(index + 1)}
                  size="large"
                >
                  <AddBox />
                </IconButton>
                <IconButton
                  tooltipProps={{ title: "Remove this definition" }}
                  className="remove_definition_btn"
                  onClick={() => removeDefinition(index, newName)}
                  size="large"
                >
                  <RemoveBox />
                </IconButton>
              </Grid>
            </Grid>
            <Grid item className="messages">
              {noMatchesFound && !definitionIsDirty && (
                <>
                  <Typography variant="caption" color="error" style={{ fontSize: 12 }}>
                    No matches found, change parameters and try again.
                  </Typography>
                  <Button
                    variant="text"
                    color="inherit"
                    style={{ fontSize: 12, textTransform: "capitalize", margin: 5, padding: 0 }}
                    onClick={() => setNoMatchesFound(false)}
                  >
                    Clear Message
                  </Button>
                </>
              )}
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
